import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  getBulkSendToChannelStatus,
  setBulkSendToChannelJob,
} from "../../../../actions/blankProducts";
import { setGlobalAlert } from "../../../../actions/common";

export const BULK_SEND_TO_CHANNEL_JOB_KEY = "riverr.blankBulkSendJob";
const POLL_MS = 2000;
const POLL_ERROR_MAX_RETRIES = 30; // ~1 min of transient network errors before giving up
const POLL_STALE_JOB_MS = 6000; // slower poll while job may still be running server-side

/**
 * App-level poller for async blank bulk send-to-channel jobs.
 * Keeps running across route changes so users can leave Base products safely.
 */
const BulkSendToChannelJobWatcher = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const job = useSelector((state) => state?.blankProducts?.bulkSendToChannelJob);
  const pollTimerRef = useRef(null);
  const pollErrorCountRef = useRef(0);
  const lastStatusRef = useRef(null);
  const finishingRef = useRef(false);
  const [floatHidden, setFloatHidden] = useState(false);
  const onBlanksIndex =
    location.pathname === "/products/blanks" ||
    location.pathname === "/products/blanks/" ||
    location.pathname === "/all-added-blanks" ||
    location.pathname === "/all-added-blanks/";

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearStoredJob = () => {
    try {
      localStorage.removeItem(BULK_SEND_TO_CHANNEL_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  const finishJob = (status) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearPoll();
    clearStoredJob();

    dispatch(setBulkSendToChannelJob(status));

    const failed = status?.failed || 0;
    const succeeded = status?.succeeded || 0;
    const isHardFail =
      status?.status === "failed" || (failed > 0 && succeeded === 0);
    const reasonSamples = (status?.failureReasons || [])
      .map((r) => r?.reason)
      .filter(Boolean)
      .slice(0, 2);
    const reasonSuffix = reasonSamples.length
      ? ` ${reasonSamples.join(" · ")}`
      : "";

    if (isHardFail) {
      dispatch(
        setGlobalAlert({
          type: "error",
          message:
            (status?.error || "Send to channel failed. Please try again.") +
            reasonSuffix,
        })
      );
    } else if (failed > 0) {
      dispatch(
        setGlobalAlert({
          type: "warning",
          message:
            (status?.error ||
              `${succeeded} published, ${failed} failed. You can retry the failed ones.`) +
            reasonSuffix,
        })
      );
    } else {
      dispatch(
        setGlobalAlert({
          type: "success",
          message: `${succeeded || status?.total || 0} customizable product(s) published.`,
        })
      );
    }

    finishingRef.current = false;
  };

  const pollJob = async (jobId) => {
    try {
      const res = await getBulkSendToChannelStatus(jobId);
      const status = res?.data;
      if (!status) throw new Error("Unable to read send-to-channel progress.");

      pollErrorCountRef.current = 0;
      lastStatusRef.current = status;
      dispatch(setBulkSendToChannelJob(status));

      if (status.status === "processing") {
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_MS);
        return;
      }

      finishJob(status);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to check send-to-channel progress.";
      const isTransient =
        /network error|timeout|ECONNABORTED|ERR_NETWORK/i.test(message);

      if (isTransient && pollErrorCountRef.current < POLL_ERROR_MAX_RETRIES) {
        pollErrorCountRef.current += 1;
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_MS * 2);
        return;
      }

      const last = lastStatusRef.current || {};
      const mayStillBeRunning =
        last.status === "processing" &&
        (last.total || 0) > 0 &&
        (last.completed || 0) < (last.total || 0);

      if (mayStillBeRunning) {
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_STALE_JOB_MS);
        return;
      }

      clearPoll();
      clearStoredJob();
      dispatch(
        setBulkSendToChannelJob({
          jobId,
          status: "failed",
          error: message,
          total: last.total || 0,
          completed: last.completed || 0,
          succeeded: last.succeeded || 0,
          failed: last.failed || 0,
        })
      );
      dispatch(setGlobalAlert({ type: "error", message }));
    }
  };

  useEffect(() => {
    let jobId = null;
    try {
      jobId = localStorage.getItem(BULK_SEND_TO_CHANNEL_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
    if (jobId) {
      dispatch(
        setBulkSendToChannelJob({
          jobId,
          status: "processing",
          total: 0,
          completed: 0,
          succeeded: 0,
          failed: 0,
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    const jobId = job?.jobId;
    if (!jobId || job?.status !== "processing") {
      return clearPoll;
    }

    setFloatHidden(false);
    pollErrorCountRef.current = 0;
    clearPoll();
    pollJob(jobId);
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.jobId, job?.status === "processing"]);

  if (!job || job.status !== "processing" || floatHidden || onBlanksIndex) {
    return null;
  }

  const total = job.total || 0;
  const completed = job.completed || 0;
  const percent =
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

  return (
    <div className="bp-bulk-send-float" role="status" aria-live="polite">
      <strong>Building customizable products</strong>
      <div>
        Building {completed} of {total || "…"} · {percent}%
      </div>
      <div className="bp-bulk-send-float__track">
        <div
          className="bp-bulk-send-float__fill"
          style={{ width: `${percent}%` }}
        />
      </div>
      <button
        type="button"
        className="bp-bulk-send-float__dismiss"
        onClick={() => setFloatHidden(true)}
      >
        Hide
      </button>
    </div>
  );
};

export default BulkSendToChannelJobWatcher;
